import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCustomer } from './Detail';
import { listCustomerOrders } from '@vn-propane/shared/client';
import type { OrderSummary, ErrorResponse, OrderStatus } from '@vn-propane/shared/client';
import { Badge } from '@/components/ui/badge';
import { Package, Truck, CheckCircle, CheckCircle2, XCircle, Clock, Store, CreditCard, Phone, MapPin } from 'lucide-react';
import { formatPrice } from '@/lib/utils';

export default function CustomerOrders() {
  const { t } = useTranslation(['admin', 'common']);
  const { id: orgId, customerId } = useParams<{ id: string; customerId: string }>();
  const { customer, organization } = useCustomer();
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    if (!orgId || !customerId) return;

    setLoading(true);
    try {
      const result = await listCustomerOrders({
        path: { org_id: orgId, customer_id: customerId },
      });

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setError(errorResponse.error?.message || t('orders.failedToLoad'));
        return;
      }

      if (result.data) {
        setOrders(result.data.data);
      }
      setError(null);
    } catch (err) {
      setError(t('orders.failedToLoad'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [orgId, customerId, t]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statusConfig: Record<OrderStatus, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive'; icon: typeof Clock }> = {
    pending: { label: t('orders.statusPending'), variant: 'secondary', icon: Clock },
    assigned: { label: t('orders.statusAssigned'), variant: 'default', icon: Truck },
    in_progress: { label: t('orders.statusInProgress'), variant: 'default', icon: Truck },
    arrived: { label: t('orders.statusArrived'), variant: 'default', icon: Truck },
    delivered: { label: t('orders.statusDelivered'), variant: 'outline', icon: CheckCircle },
    completed: { label: t('orders.statusCompleted'), variant: 'outline', icon: CheckCircle2 },
    cancelled: { label: t('orders.statusCancelled'), variant: 'destructive', icon: XCircle },
  };

  if (!customer) {
    return null;
  }

  if (loading) {
    return <div className="flex justify-center p-8">{t('common:labels.loading')}</div>;
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">{t('customers.orderHistory')}</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {t('customers.orderHistoryDescription')}
        </p>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>{t('customers.noOrdersYet')}</p>
          <p className="text-sm">{t('customers.ordersWillAppear')}</p>
        </div>
      ) : (
        <div className="border rounded-lg divide-y">
          {orders.map((order) => {
            const config = statusConfig[order.status] || statusConfig.pending;
            const StatusIcon = config.icon;
            return (
              <Link
                key={order.id}
                to={`/organizations/${orgId}/orders/${order.id}`}
                className="block p-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm font-mono">
                        #{order.id.slice(-8).toUpperCase()}
                      </span>
                      <Badge variant={config.variant} className="flex items-center gap-1">
                        <StatusIcon className="w-3 h-3" />
                        {config.label}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      {order.customer_phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" />
                          <span>{order.customer_phone}</span>
                        </div>
                      )}
                      {order.gas_station_name && (
                        <div className="flex items-center gap-1">
                          <Store className="w-3.5 h-3.5" />
                          <span>{order.gas_station_name}</span>
                        </div>
                      )}
                      {order.driver_name ? (
                        <div className="flex items-center gap-1">
                          <Truck className="w-3.5 h-3.5" />
                          <span>{order.driver_name}</span>
                        </div>
                      ) : order.status === 'pending' && (
                        <div className="flex items-center gap-1 text-amber-600">
                          <Truck className="w-3.5 h-3.5" />
                          <span>{t('orders.noDriverAssigned')}</span>
                        </div>
                      )}
                    </div>
                    {order.customer_address && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{order.customer_address}</span>
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      {t('orders.itemCount', { count: order.item_count })} - {formatDate(order.created_at)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{formatPrice(order.total_amount.toString(), organization.currency)}</div>
                    {order.is_paid ? (
                      <div className="flex items-center gap-1 text-xs text-green-600 justify-end mt-0.5">
                        <CreditCard className="w-3 h-3" />
                        <span>{t('orders.paid')}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-xs text-amber-600 justify-end mt-0.5">
                        <CreditCard className="w-3 h-3" />
                        <span>{t('orders.unpaid')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
